import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Tabs,
  Row,
  Col,
  Statistic,
  Timeline,
  Progress,
  List,
  Badge,
  message,
  Popconfirm,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Alert,
  Steps,
  Radio,
  Divider,
  Tooltip,
  Table,
  Descriptions,
} from 'antd';
import {
  ApartmentOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SettingOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  ReloadOutlined,
  ExportOutlined,
  ImportOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  StopOutlined,
  BarChartOutlined,
} from '@ant-design/icons';

const { Option } = Select;

const WorkflowManagement: React.FC = () => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  const [designModalVisible, setDesignModalVisible] = useState(false);
  const [monitorModalVisible, setMonitorModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [testProgress, setTestProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  // 新增工作流
  const handleAddWorkflow = () => {
    setSelectedWorkflow(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  // 编辑工作流
  const handleEditWorkflow = (record: any) => {
    setSelectedWorkflow(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  // 保存工作流
  const handleSaveWorkflow = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedWorkflow) {
        message.success('工作流更新成功！');
      } else {
        message.success('工作流创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除工作流
  const handleDeleteWorkflow = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(`已删除工作流：${record.name}`);
    } catch (error) {
      message.error('删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 查看工作流详情
  const handleViewWorkflow = (record: any) => {
    setSelectedWorkflow(record);
    setMonitorModalVisible(true);
  };

  // 启动工作流
  const handleStartWorkflow = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      message.success(`工作流 ${record.name} 已启动`);
    } catch (error) {
      message.error('启动失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 停止工作流
  const handleStopWorkflow = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      message.success(`工作流 ${record.name} 已停止`);
    } catch (error) {
      message.error('停止失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 复制工作流
  const handleCopyWorkflow = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      message.success(`已复制工作流：${record.name}`);
    } catch (error) {
      message.error('复制失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 测试工作流
  const handleTestWorkflow = (record: any) => {
    setSelectedWorkflow(record);
    setTestProgress(0);
    setCurrentStep(0);
    setTestModalVisible(true);
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的工作流');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已删除 ${selectedRowKeys.length} 个工作流`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 运行测试
  const handleRunTest = async () => {
    setLoading(true);
    try {
      const steps = [
        { step: 0, title: '初始化工作流', status: 'process' },
        { step: 1, title: '加载配置参数', status: 'process' },
        { step: 2, title: '执行流程步骤', status: 'process' },
        { step: 3, title: '验证执行结果', status: 'process' },
        { step: 4, title: '生成测试报告', status: 'finish' },
      ];
      
      for (let i = 0; i < steps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setCurrentStep(i);
        setTestProgress((i + 1) * 20);
      }
      
      message.success('工作流测试完成！');
    } catch (error) {
      message.error('测试失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const workflowData = [
    {
      id: 1,
      name: '用户注册流程',
      description: '处理新用户注册的完整流程',
      status: 'active',
      executions: 1256,
      successRate: 94.5,
      lastRun: '2025-10-23 14:30',
    },
    {
      id: 2,
      name: '游戏引导流程',
      description: '引导新用户了解游戏规则',
      status: 'active',
      executions: 856,
      successRate: 92.3,
      lastRun: '2025-10-23 14:25',
    },
  ];

  const workflowStats = [
    { title: '总工作流数', value: 12, icon: <ApartmentOutlined /> },
    { title: '活跃工作流', value: 10, icon: <ApartmentOutlined /> },
    { title: '今日执行', value: 3240, icon: <ApartmentOutlined /> },
    { title: '平均成功率', value: '92.5%', icon: <ApartmentOutlined /> },
  ];

  const executionHistory = [
    {
      id: 1,
      workflow: '用户注册流程',
      status: 'success',
      startTime: '2025-10-23 14:30:15',
      endTime: '2025-10-23 14:30:18',
      duration: '3秒',
      steps: 5,
    },
    {
      id: 2,
      workflow: '游戏引导流程',
      status: 'success',
      startTime: '2025-10-23 14:25:10',
      endTime: '2025-10-23 14:25:15',
      duration: '5秒',
      steps: 7,
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>工作流管理</h1>
        <p>定义工作流，完成复杂功能，实现意图识别、智能体调用、内容组装的完整能力</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {workflowStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number">{stat.value}</div>
                <div className="stats-label">{stat.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* 工作流列表 */}
        <Col xs={24} lg={16}>
          <Card
            title="工作流列表"
            extra={
              <Space>
                <Tooltip title="导入工作流">
                  <Button icon={<ImportOutlined />} onClick={() => message.info('导入功能开发中...')}>
                    导入
                  </Button>
                </Tooltip>
                <Tooltip title="导出工作流">
                  <Button icon={<ExportOutlined />} onClick={() => message.info('导出功能开发中...')}>
                    导出
                  </Button>
                </Tooltip>
                <Tooltip title="工作流配置">
                  <Button icon={<SettingOutlined />} onClick={() => setDesignModalVisible(true)}>
                    配置
                  </Button>
                </Tooltip>
                <Tooltip title="创建工作流">
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddWorkflow}>
                    创建工作流
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <List
              dataSource={workflowData}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Tooltip title="查看详情">
                      <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewWorkflow(item)}>
                        查看
                      </Button>
                    </Tooltip>,
                    <Tooltip title="测试工作流">
                      <Button type="link" icon={<BarChartOutlined />} onClick={() => handleTestWorkflow(item)}>
                        测试
                      </Button>
                    </Tooltip>,
                    <Tooltip title="编辑工作流">
                      <Button type="link" icon={<EditOutlined />} onClick={() => handleEditWorkflow(item)}>
                        编辑
                      </Button>
                    </Tooltip>,
                    <Tooltip title="复制工作流">
                      <Button type="link" icon={<CopyOutlined />} onClick={() => handleCopyWorkflow(item)}>
                        复制
                      </Button>
                    </Tooltip>,
                    item.status === 'active' ? (
                      <Tooltip title="停止工作流">
                        <Button type="link" icon={<StopOutlined />} onClick={() => handleStopWorkflow(item)} danger>
                          停止
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip title="启动工作流">
                        <Button type="link" icon={<PlayCircleOutlined />} onClick={() => handleStartWorkflow(item)}>
                          启动
                        </Button>
                      </Tooltip>
                    ),
                    <Popconfirm
                      title="确定要删除这个工作流吗？"
                      onConfirm={() => handleDeleteWorkflow(item)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Tooltip title="删除工作流">
                        <Button type="link" icon={<DeleteOutlined />} danger>
                          删除
                        </Button>
                      </Tooltip>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<ApartmentOutlined style={{ fontSize: 24 }} />}
                    title={
                      <Space>
                        <span>{item.name}</span>
                        <Tag color={item.status === 'active' ? 'green' : 'red'}>
                          {item.status === 'active' ? '活跃' : '暂停'}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div>{item.description}</div>
                        <div style={{ marginTop: 8 }}>
                          <Space>
                            <span>执行次数: {item.executions}</span>
                            <span>成功率: {item.successRate}%</span>
                            <span>最后运行: {item.lastRun}</span>
                          </Space>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* 执行历史 */}
        <Col xs={24} lg={8}>
          <Card title="执行历史" extra={<Button type="link">查看全部</Button>}>
            <Timeline
              items={executionHistory.map((item) => ({
                color: item.status === 'success' ? 'green' : 'red',
                children: (
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{item.workflow}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {item.startTime} - {item.duration}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      步骤数: {item.steps}
                    </div>
                  </div>
                ),
              }))}
            />
          </Card>
        </Col>
      </Row>

      {/* 工作流设计器模态框 */}
      <Modal
        title="工作流设计器"
        open={designModalVisible}
        onCancel={() => setDesignModalVisible(false)}
        width={1200}
        footer={[
          <Button key="cancel" onClick={() => setDesignModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" type="primary" onClick={() => {
            message.success('工作流配置已保存！');
            setDesignModalVisible(false);
          }}>
            保存
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', height: 600 }}>
          {/* 左侧节点面板 */}
          <div style={{ width: 200, background: '#fafafa', padding: 16, borderRight: '1px solid #d9d9d9' }}>
            <h4 style={{ marginBottom: 16 }}>节点库</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div 
                style={{ 
                  padding: 12, 
                  background: '#fff', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 6,
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                onClick={() => message.info('开始节点已添加')}
              >
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>开始</div>
                <div style={{ fontSize: 12, color: '#666' }}>Start</div>
              </div>
              <div 
                style={{ 
                  padding: 12, 
                  background: '#fff', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 6,
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                onClick={() => message.info('条件判断节点已添加')}
              >
                <div style={{ fontWeight: 'bold', color: '#1890ff' }}>条件判断</div>
                <div style={{ fontSize: 12, color: '#666' }}>If/Else</div>
              </div>
              <div 
                style={{ 
                  padding: 12, 
                  background: '#fff', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 6,
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                onClick={() => message.info('AI处理节点已添加')}
              >
                <div style={{ fontWeight: 'bold', color: '#722ed1' }}>AI处理</div>
                <div style={{ fontSize: 12, color: '#666' }}>AI Process</div>
              </div>
              <div 
                style={{ 
                  padding: 12, 
                  background: '#fff', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 6,
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                onClick={() => message.info('API调用节点已添加')}
              >
                <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>API调用</div>
                <div style={{ fontSize: 12, color: '#666' }}>API Call</div>
              </div>
              <div 
                style={{ 
                  padding: 12, 
                  background: '#fff', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 6,
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                onClick={() => message.info('结束节点已添加')}
              >
                <div style={{ fontWeight: 'bold', color: '#f5222d' }}>结束</div>
                <div style={{ fontSize: 12, color: '#666' }}>End</div>
              </div>
            </div>
          </div>

          {/* 中间画布区域 */}
          <div style={{ flex: 1, position: 'relative', background: '#fff' }}>
            <div style={{ 
              position: 'absolute', 
              top: 20, 
              left: 20, 
              right: 20, 
              bottom: 20, 
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              background: '#fafafa'
            }}>
              {/* 示例工作流 */}
              <div style={{ position: 'relative', height: '100%', padding: 20 }}>
                {/* 开始节点 */}
                <div style={{
                  position: 'absolute',
                  top: 50,
                  left: 50,
                  width: 80,
                  height: 60,
                  background: '#52c41a',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }} onClick={() => message.info('点击了开始节点')}>
                  开始
                </div>

                {/* 连接线 */}
                <div style={{
                  position: 'absolute',
                  top: 110,
                  left: 90,
                  width: 2,
                  height: 40,
                  background: '#d9d9d9'
                }}></div>

                {/* 条件判断节点 */}
                <div style={{
                  position: 'absolute',
                  top: 150,
                  left: 30,
                  width: 100,
                  height: 60,
                  background: '#1890ff',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }} onClick={() => message.info('点击了条件判断节点')}>
                  条件判断
                </div>

                {/* 连接线 */}
                <div style={{
                  position: 'absolute',
                  top: 210,
                  left: 80,
                  width: 2,
                  height: 40,
                  background: '#d9d9d9'
                }}></div>

                {/* AI处理节点 */}
                <div style={{
                  position: 'absolute',
                  top: 250,
                  left: 50,
                  width: 100,
                  height: 60,
                  background: '#722ed1',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }} onClick={() => message.info('点击了AI处理节点')}>
                  AI处理
                </div>

                {/* 连接线 */}
                <div style={{
                  position: 'absolute',
                  top: 310,
                  left: 100,
                  width: 2,
                  height: 40,
                  background: '#d9d9d9'
                }}></div>

                {/* 结束节点 */}
                <div style={{
                  position: 'absolute',
                  top: 350,
                  left: 70,
                  width: 80,
                  height: 60,
                  background: '#f5222d',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }} onClick={() => message.info('点击了结束节点')}>
                  结束
                </div>

                {/* 提示文字 */}
                <div style={{
                  position: 'absolute',
                  bottom: 20,
                  left: 20,
                  right: 20,
                  textAlign: 'center',
                  color: '#999',
                  fontSize: 12
                }}>
                  点击左侧节点添加到画布，点击画布中的节点进行编辑
                </div>
              </div>
            </div>
          </div>

          {/* 右侧属性面板 */}
          <div style={{ width: 250, background: '#fafafa', padding: 16, borderLeft: '1px solid #d9d9d9' }}>
            <h4 style={{ marginBottom: 16 }}>属性配置</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>节点名称</label>
                <Input placeholder="输入节点名称" />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>节点类型</label>
                <Select style={{ width: '100%' }} defaultValue="process">
                  <Option value="start">开始</Option>
                  <Option value="process">处理</Option>
                  <Option value="condition">条件</Option>
                  <Option value="end">结束</Option>
                </Select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>超时时间(秒)</label>
                <InputNumber style={{ width: '100%' }} defaultValue={30} min={1} max={300} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>重试次数</label>
                <InputNumber style={{ width: '100%' }} defaultValue={3} min={0} max={10} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>描述</label>
                <Input.TextArea rows={3} placeholder="输入节点描述" />
              </div>
              <Button type="primary" style={{ marginTop: 8 }} onClick={() => message.success('节点配置已保存')}>
                保存配置
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* 执行监控模态框 */}
      <Modal
        title="执行监控"
        open={monitorModalVisible}
        onCancel={() => setMonitorModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setMonitorModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="实时监控" key="1">
            <div style={{ marginBottom: 16 }}>
              <h4>当前执行状态</h4>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic title="正在执行" value={3} />
                </Col>
                <Col span={8}>
                  <Statistic title="等待中" value={5} />
                </Col>
                <Col span={8}>
                  <Statistic title="已完成" value={1256} />
                </Col>
              </Row>
            </div>
            <Divider />
            <div>
              <h4>执行队列</h4>
              <List
                size="small"
                dataSource={[
                  { workflow: '用户注册流程', status: 'running', progress: 60 },
                  { workflow: '游戏引导流程', status: 'waiting', progress: 0 },
                  { workflow: '充值处理流程', status: 'running', progress: 80 },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.workflow}
                      description={
                        <div>
                          <Tag color={item.status === 'running' ? 'blue' : 'orange'}>
                            {item.status === 'running' ? '执行中' : '等待中'}
                          </Tag>
                          {item.status === 'running' && (
                            <Progress percent={item.progress} size="small" style={{ marginLeft: 16 }} />
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="性能分析" key="2">
            <div>
              <h4>性能指标</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>平均执行时间</span>
                      <span>2.3秒</span>
                    </div>
                    <Progress percent={75} />
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>成功率</span>
                      <span>94.5%</span>
                    </div>
                    <Progress percent={94.5} strokeColor="#52c41a" />
                  </div>
                </Col>
              </Row>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      {/* 编辑工作流模态框 */}
      <Modal
        title={selectedWorkflow ? '编辑工作流' : '新增工作流'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            保存
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveWorkflow}
        >
          <Form.Item
            name="name"
            label="工作流名称"
            rules={[{ required: true, message: '请输入工作流名称' }]}
          >
            <Input placeholder="请输入工作流名称" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="工作流描述"
            rules={[{ required: true, message: '请输入工作流描述' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入工作流描述" />
          </Form.Item>
          
          <Form.Item
            name="triggerType"
            label="触发类型"
            rules={[{ required: true, message: '请选择触发类型' }]}
          >
            <Select placeholder="请选择触发类型">
              <Select.Option value="manual">手动触发</Select.Option>
              <Select.Option value="event">事件触发</Select.Option>
              <Select.Option value="schedule">定时触发</Select.Option>
              <Select.Option value="api">API调用</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="priority"
            label="优先级"
            initialValue={5}
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="enabled"
            label="启用状态"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 测试工作流模态框 */}
      <Modal
        title="工作流测试"
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setTestModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="test" 
            type="primary" 
            icon={<BarChartOutlined />}
            onClick={handleRunTest}
            loading={loading}
          >
            运行测试
          </Button>
        ]}
      >
        {selectedWorkflow && (
          <div>
            <Alert
              message="工作流测试"
              description={`正在测试工作流：${selectedWorkflow.name}`}
              type="info"
              style={{ marginBottom: 16 }}
            />
            
            <Steps
              current={currentStep}
              items={[
                { title: '初始化工作流' },
                { title: '加载配置参数' },
                { title: '执行流程步骤' },
                { title: '验证执行结果' },
                { title: '生成测试报告' },
              ]}
              style={{ marginBottom: 24 }}
            />
            
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={testProgress}
                format={() => `${testProgress}%`}
              />
            </div>
            
            {testProgress === 100 && (
              <Alert
                message="测试完成"
                description="工作流测试已成功完成，所有步骤执行正常。"
                type="success"
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WorkflowManagement;
